/**
 * Whether a rejected fetch was OUR cancellation (effect cleanup) rather than a real failure.
 *
 * @remarks Two ways to be sure, because the error alone is not reliable. `fetch` raises a
 * `DOMException` named `AbortError` when a controller aborts — but only in some browsers. SAFARI
 * reports a load cancelled in flight as `TypeError: Load failed`, indistinguishable by name from a
 * genuine network or CORS failure. A member who navigates away mid-request therefore produces a
 * Sentry error for a request the app itself has just cancelled.
 *
 * The signal is the authority: if we aborted, any rejection that follows is our doing, whatever the
 * browser called it. Pass the signal from the same request and the check stops depending on wording.
 *
 * Duck-typed on `name` rather than `instanceof DOMException` so it still matches a plain object, and
 * takes `unknown` because a rejection can be any thrown value.
 */
export function isCancellation(reason: unknown, signal?: AbortSignal): boolean {
  if (signal?.aborted === true) return true;
  return (
    typeof reason === 'object' &&
    reason !== null &&
    'name' in reason &&
    reason.name === 'AbortError'
  );
}
